import React, { Component } from 'react';
import styles from './ReviewForm.scss';
import classNames from 'classnames/bind';
import { Switch, Route } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  Form,
  FormInput,
  Label,
  RadioInput,
  StarInput,
  Textarea,
  Spinner,
} from '../common';
import history from '~/history';

const cx = classNames.bind(styles);

const initialState = {
  inputs: {
    years: null,
    expertise: 0,
    reliability: 0,
    responsiveness: 0,
    price: 0,
    overall: 0,
    text: '',
    recommended: null,
    note: '',
  },
  validStatuses: {
    years: true,
    expertise: true,
    reliability: true,
    responsiveness: true,
    price: true,
    overall: true,
    text: true,
    recommended: true,
    note: true,
  },
};

export class ReviewForm extends Component {
  state = initialState;

  formValidator = () => {
    const { inputs, validStatuses } = this.state;

    const newState = Object.keys(validStatuses).reduce((acc, key) => {
      acc[key] = this.inputValidator(key, inputs[key]);
      return acc;
    }, {});

    this.setState({ validStatuses: newState });

    return Object.values(newState).every(value => value);
  };

  sanitize = text => {
    return text ? text.replace(/(\s)\1+/g, '$1').trim() : '';
  };

  inputValidator = (name, value) => {
    switch (name) {
      case 'expertise':
      case 'reliability':
      case 'responsiveness':
      case 'price':
      case 'overall':
        return value > 0;

      case 'text':
        const text = this.sanitize(value);
        return text !== '' && text.length >= 30 && text.length < 400;

      case 'years':
      case 'recommended':
        return value !== null;

      case 'note':
        return this.sanitize(value).length < 400;

      default:
        return true;
    }
  };

  submit = id => {
    const { ReviewFormActions } = this.props;
    const { inputs } = this.state;

    ReviewFormActions.submit(
      id,
      Object.assign({}, inputs, {
        text: this.sanitize(inputs.text),
        note: this.sanitize(inputs.note),
      })
    )
      .then(val => {
        alert(
          '리뷰가 등록되었습니다.\n운영자 확인 후 리뷰 노출 및 캐시노트\n고급형 이용권을 제공해드리겠습니다.'
        );
        this.setState(initialState);
        history.push('/reviews/new');
      })
      .catch(error => {
        alert('이미 평가를 완료한 세무대리인입니다.');
        history.push('/reviews/new');
      });
  };

  onValidationFail = () => {
    alert('필수 문항을 모두 입력해주시고, 입력길이 제한을 확인해주세요.');
  };

  handleInputChange = (name, value) => {
    const { inputs, validStatuses } = this.state;

    if (value !== inputs[name]) {
      this.setState({
        inputs: {
          ...inputs,
          [name]: value,
        },
        validStatuses: {
          ...validStatuses,
          [name]: this.inputValidator(name, value),
        },
      });
    }
  };

  reviewStartText = () => (
    <div className={cx('review-form-start__text')}>
      <span>세무대리인을 평가하고</span>
      <span>
        <span className="u-color-primary u-bold">캐시노트 고급형 이용권</span>을
      </span>
      <span>받아가세요!</span>
    </div>
  );

  reviewStartHeader = () => (
    <div className={cx('review-form-start__header')}>
      내 세무대리인
      <div
        className={cx('review-form-start__add-agent')}
        onClick={() => {
          if (
            confirm(
              '세무대리인을 이용하는 사업장의\n홈택스를 연동하시면\n세무대리인이 추가됩니다.'
            )
          ) {
            window.location = 'https://app.cashnote.kr/m/businesses';
          }
        }}
      >
        추가하기
        <i className="fa fa-chevron-right" />
      </div>
    </div>
  );

  reviewStartAgents = agents =>
    agents.size > 0 ? (
      <div className={cx('review-form-start__agents')}>
        {agents.map(agent => (
          <div
            key={agent.id}
            className={cx('review-form-start__agent', {
              'review-form-start__agent--completed': agent.reviewed,
            })}
            onClick={() =>
              !agent.reviewed && history.push(`/reviews/new/${agent.id}`)
            }
          >
            <div>
              <div className={cx('review-form-start__agent-name')}>
                {agent.name}
              </div>
              <div className={cx('review-form-start__agent-description')}>
                {agent.reviewed
                  ? '평가완료'
                  : '지금 평가하고 무료이용권을 받으세요!'}
              </div>
            </div>
            <i className="fa fa-chevron-right" />
          </div>
        ))}
        <div className={cx('review-form-start__time-span')}>
          <i className="fa fa-info-circle" />
          설문은 약 1~2분 정도 소요됩니다.
        </div>
      </div>
    ) : (
      this.reviewStartAgentsEmpty()
    );

  reviewStartAgentsEmpty = () => (
    <div className={cx('review-form-start__agents-empty')}>
      <div className={cx('review-form-start__agents-empty-text')}>
        <span>평가할 세무대리인이 없습니다.</span>
        <span>세무대리인을 이용하는 사업장의 홈택스를</span>
        <span>연동하시면 세무대리인을 평가할 수 있습니다.</span>
      </div>
      <Button
        square
        block
        background="secondary"
        className={cx('review-form-start__agents-empty-cta')}
        onClick={() =>
          (window.location = 'https://app.cashnote.kr/m/businesses')
        }
      >
        홈택스 등록하기
      </Button>
    </div>
  );

  yearsInput = name => (
    <FormInput
      required
      valid={this.state.validStatuses.years}
      label={
        <Label>
          {name}의 <b>이용기간</b>을 선택해주세요.
        </Label>
      }
      input={
        <RadioInput
          onChange={v => this.handleInputChange('years', v)}
          value={this.state.inputs.years}
          options={[
            {
              value: 'less_than_one',
              label: '1년 미만',
            },
            {
              value: 'less_than_three',
              label: '1년 이상 ~ 3년 미만',
            },
            {
              value: 'less_than_five',
              label: '3년 이상 ~ 5년 미만',
            },
            {
              value: 'more_than_five',
              label: '5년 이상',
            },
          ]}
        />
      }
    />
  );

  expertiseInput = name => (
    <FormInput
      required
      valid={this.state.validStatuses.expertise}
      label={
        <Label>
          {name}의 "<b>전문성</b>"을 평가해주세요.
        </Label>
      }
      descriptions={[
        '* 세무에 대한 지식과 경험이 풍부하고, 절세 방법을 잘 알고 있음',
      ]}
      input={
        <StarInput
          onChange={v => this.handleInputChange('expertise', v)}
          value={this.state.inputs.expertise}
        />
      }
    />
  );

  reliabilityInput = name => (
    <FormInput
      required
      valid={this.state.validStatuses.reliability}
      label={
        <Label>
          {name}의 "<b>신뢰성</b>"을 평가해주세요.
        </Label>
      }
      descriptions={['* 신고 기한을 잘 준수하고, 정확한 세금 신고를 함']}
      input={
        <StarInput
          onChange={v => this.handleInputChange('reliability', v)}
          value={this.state.inputs.reliability}
        />
      }
    />
  );

  responsivenessInput = name => (
    <FormInput
      required
      valid={this.state.validStatuses.responsiveness}
      label={
        <Label>
          {name}의 "<b>응답성</b>"을 평가해주세요.
        </Label>
      }
      descriptions={['* 친절한 응대, 신속하고 적절한 피드백을 줌']}
      input={
        <StarInput
          onChange={v => this.handleInputChange('responsiveness', v)}
          value={this.state.inputs.responsiveness}
        />
      }
    />
  );

  priceInput = name => (
    <FormInput
      required
      valid={this.state.validStatuses.price}
      label={
        <Label>
          {name}의 "<b>가격만족도</b>"를 평가해주세요.
        </Label>
      }
      descriptions={['* 가격이 합리적이고 지불한 비용만큼 서비스를 받음']}
      input={
        <StarInput
          onChange={v => this.handleInputChange('price', v)}
          value={this.state.inputs.price}
        />
      }
    />
  );

  overallInput = () => (
    <FormInput
      required
      valid={this.state.validStatuses.overall}
      label={
        <Label>
          <b>총점</b>을 입력해주세요.
        </Label>
      }
      descriptions={['* 전체적인 만족도로 평가해주세요.']}
      input={
        <StarInput
          onChange={v => this.handleInputChange('overall', v)}
          value={this.state.inputs.overall}
        />
      }
    />
  );

  textInput = () => {
    const { inputs, validStatuses } = this.state;
    let error = '';
    const text = this.sanitize(inputs.text);
    if (text === '') {
      error = '필수로 응답해야 하는 문항입니다.';
    } else if (text.length < 30) {
      error = '30자 이상 입력해주세요.';
    } else if (text.length > 400) {
      error = '400자까지만 쓰실 수 있습니다.';
    }

    return (
      <FormInput
        required
        valid={validStatuses.text}
        label={
          <Label>
            <b>총평</b>을 입력해주세요.
          </Label>
        }
        descriptions={['(최소 30자 이상)']}
        input={
          <Textarea
            onChange={v => this.handleInputChange('text', v)}
            value={inputs.text}
          />
        }
        error={error}
      />
    );
  };

  recommendedInput = () => (
    <FormInput
      required
      valid={this.state.validStatuses.recommended}
      label={<Label>이 세무대리인을 주변 사람들에게 추천하시겠습니까?</Label>}
      input={
        <RadioInput
          onChange={v => this.handleInputChange('recommended', v)}
          value={this.state.inputs.recommended}
          options={[
            {
              value: true,
              label: '예',
            },
            {
              value: false,
              label: '아니오',
            },
          ]}
        />
      }
    />
  );

  noteInput = () => (
    <FormInput
      valid={this.state.validStatuses.note}
      label={<Label>세무대리인에게 하고싶은 말</Label>}
      descriptions={[
        '* 본 항목은 익명으로 세무대리인에게 전달 될 수 있습니다. 서비스 향상에 도움이 될만한 코멘트가 있으면 작성해주세요.',
        '* 본 항목은 사이트에 노출되지 않으며 리뷰에 영향을 주지 않습니다.',
        '* 작성글 이외의 어떠한 정보도 세무대리인에게 제공되지 않습니다.',
      ]}
      input={
        <Textarea
          onChange={v => this.handleInputChange('note', v)}
          value={this.state.inputs.note}
        />
      }
      error="400자까지만 쓰실 수 있습니다."
    />
  );

  render() {
    const { reviewForm, submittingReview, isLoading } = this.props;
    const { reviewable } = reviewForm;

    return (
      <Container>
        <Header title="세무대리인 평가하기" />
        <Switch>
          <Route
            path="/reviews/new/:agentId"
            render={({ match }) => {
              const { agentId } = match.params;
              const agent = reviewable.find(
                agent => parseInt(agent.id) === parseInt(agentId)
              );
              return agent ? (
                <div className={cx('review-form-survey')}>
                  <Form
                    onSubmit={() => this.submit(agentId)}
                    validator={this.formValidator}
                    onValidationFail={this.onValidationFail}
                    submitting={submittingReview}
                  >
                    {this.yearsInput(agent.name)}
                    {this.expertiseInput(agent.name)}
                    {this.reliabilityInput(agent.name)}
                    {this.responsivenessInput(agent.name)}
                    {this.priceInput(agent.name)}
                    {this.overallInput()}
                    {this.textInput()}
                    {this.recommendedInput()}
                    {this.noteInput()}
                  </Form>
                </div>
              ) : null;
            }}
          />
          <Route
            exact
            path="/reviews/new"
            render={() => (
              <div className={cx('review-form-start')}>
                {this.reviewStartText()}
                {this.reviewStartHeader()}
                {isLoading !== false ? (
                  <Spinner />
                ) : (
                  this.reviewStartAgents(reviewable)
                )}
              </div>
            )}
          />
        </Switch>
      </Container>
    );
  }
}

export default ReviewForm;



// WEBPACK FOOTER //
// ./src/components/ReviewForm/ReviewForm.js